import { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Grid,
  StyledEngineProvider,
  ThemeProvider,
  Typography
} from "@mui/material";

import {
  faUser,
  faMapMarkerAlt,
  faBuilding,
  faClock,
  faCalendarCheck
} from "@fortawesome/free-solid-svg-icons";

import mainTheme from "@aclymatepackages/themes";

import useCsvUploader from "../../hooks/csvUploader";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import {
  doesFieldExist,
  createErrorObj,
  useSharedFormLoading
} from "../../../helpers/components/inputs";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { getAccountId } from "../../../helpers/accountHelpers";
import {
  useAccountData,
  useMutateCachedCollection
} from "../../../helpers/firebase";

import EmployeeBulkImportPreview from "../../modules/employees/EmployeeBulkImportPreview";
import {
  buildEffectiveDecisions,
  isReadyToImport
} from "./useEmployeeBulkImportUploader.helpers";

const fieldOptions = [
  { label: "Last Name", value: "lastName" },
  { label: "First Name", value: "firstName" },
  { label: "Email", value: "email" },
  { label: "Department / Cost Center", value: "department" },
  { label: "Home City", value: "homeCity" },
  { label: "Home State", value: "homeState" },
  { label: "Work City", value: "workCity" },
  { label: "Work State", value: "workState" },
  { label: "Mode (FT Office / Hybrid / Remote)", value: "mode" },
  { label: "Days/wk in office (Hybrid)", value: "daysHybridInOffice" },
  { label: "Start Date", value: "startDate" }
];

const knownFields = [
  { header: "last_name", field: "lastName" },
  { header: "last name", field: "lastName" },
  { header: "first name", field: "firstName" },
  { header: "first_name", field: "firstName" },
  { header: "email", field: "email" },
  { header: "department", field: "department" },
  { header: "cc1", field: "department" },
  { header: "cost center", field: "department" },
  { header: "city", field: "homeCity" },
  { header: "home city", field: "homeCity" },
  { header: "state", field: "homeState" },
  { header: "home state", field: "homeState" },
  { header: "work city", field: "workCity" },
  { header: "work location city", field: "workCity" },
  { header: "work state", field: "workState" },
  { header: "work location state", field: "workState" },
  { header: "hybrid/remote/ft office", field: "mode" },
  { header: "hybrid/ remote/ ft office", field: "mode" },
  { header: "office mode", field: "mode" },
  { header: "# of days hybird", field: "daysHybridInOffice" },
  { header: "# of days hybrid", field: "daysHybridInOffice" },
  { header: "days hybrid", field: "daysHybridInOffice" },
  { header: "start date", field: "startDate" },
  { header: "start_date", field: "startDate" },
  { header: "hire date", field: "startDate" },
  { header: "hire_date", field: "startDate" },
  { header: "date of hire", field: "startDate" }
];

const requiredFields = [
  {
    label: "Last Name",
    description: "Required — used to identify each employee.",
    icon: faUser
  },
  {
    label: "Mode",
    description:
      "Required — must be one of: FT Office, Hybrid, or Remote (and # of days hybrid if Hybrid).",
    icon: faClock
  },
  {
    label: "Home City + State",
    description:
      "Optional but recommended. Without a home city we'll use the U.S. Census average commute (~16 mi).",
    icon: faMapMarkerAlt
  },
  {
    label: "Work City + State",
    description:
      "Optional. If it matches one of your Aclymate offices we'll link to it; otherwise we'll use the CSV value as the work location.",
    icon: faBuilding
  },
  {
    label: "Start Date",
    description:
      "Optional. The date the employee started — leave blank for new hires (we'll use today's date).",
    icon: faCalendarCheck
  }
];

const fieldValidator = (headers) => {
  if (!doesFieldExist(headers, "lastName")) {
    return createErrorObj("A 'Last Name' column is required to identify rows");
  }
  if (!doesFieldExist(headers, "mode")) {
    return createErrorObj(
      "A column for 'FT Office / Hybrid / Remote' mode is required"
    );
  }
  return { success: true };
};

export const useEmployeeBulkImportUploader = ({ onClose }) => {
  const { activateSnackbar } = usePlatformLayoutContext();
  const { setFormLoading } = useSharedFormLoading();
  const [{ startDate: companyStartDate }] = useAccountData();
  const mutateCachedCollection = useMutateCachedCollection();

  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [decisions, setDecisions] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [useCompanyStartDateFallback, setUseCompanyStartDateFallback] =
    useState(true);

  const handlePreview = async ({ data: rows }) => {
    setParsedRows(rows);
    const companyId = getAccountId();
    setPreviewLoading(true);

    const response = await fetchOurApi({
      accountId: companyId,
      path: "/individuals/bulk-import-preview",
      method: "POST",
      data: { companyId, rows },
      callback: (json) => json
    }).catch((error) => ({
      success: false,
      message: error?.message || "Preview failed"
    }));

    setPreviewLoading(false);

    if (!response?.success) {
      activateSnackbar({
        message:
          response?.message ||
          "We couldn't preview these rows. Check the file and try again.",
        alert: "warning"
      });
      return;
    }

    setPreviewResult(response);
    return { isPreviewDisplayed: true };
  };

  const handleImport = async () => {
    if (!previewResult) {
      return;
    }
    const companyId = getAccountId();
    const effectiveDecisions = buildEffectiveDecisions({
      analyses: previewResult.analyses,
      decisions
    });

    setFormLoading(true);

    const fallbackStartDateIso =
      useCompanyStartDateFallback && companyStartDate
        ? new Date(
            companyStartDate.seconds
              ? companyStartDate.seconds * 1000
              : companyStartDate
          ).toISOString()
        : null;

    const response = await fetchOurApi({
      accountId: companyId,
      path: "/individuals/bulk-import",
      method: "POST",
      data: {
        companyId,
        rows: parsedRows,
        decisions: effectiveDecisions,
        fallbackStartDate: fallbackStartDateIso
      },
      callback: (json) => json
    }).catch((error) => ({
      success: false,
      message: error?.message || "Bulk import failed"
    }));

    setFormLoading(false);

    if (!response?.success) {
      activateSnackbar({
        message:
          response?.message ||
          "We couldn't import these employees. Please try again.",
        alert: "warning"
      });
      return;
    }

    setImportResult(response);

    const { summary } = response;
    const importedCount = summary.created + summary.updated;

    if (importedCount > 0) {
      await mutateCachedCollection("employees");
    }

    activateSnackbar({
      message: `Imported ${importedCount} employee${
        importedCount === 1 ? "" : "s"
      } as estimated data${
        summary.failed ? `; ${summary.failed} row(s) failed` : ""
      }${summary.skipped ? `; ${summary.skipped} skipped` : ""}.`,
      alert: summary.failed ? "warning" : "success"
    });

    if (!summary.failed) {
      onClose();
    }
  };

  const employeeBulkImportSteps = useCsvUploader({
    title: "Upload customer-supplied employee data",
    knownFields,
    fieldOptions,
    docType: "customer-uploaded-employees",
    fieldValidator,
    processData: handlePreview,
    requiredFields,
    firstStepText:
      "Use this only when employees can't fill out the survey. Upload a CSV of employee location and commute data — we'll store it as estimated data using conservative GHG-protocol defaults. Save your XLSX as CSV before uploading.",
    submitText: "Preview rows",
    setOpen: () => {},
    successMessage:
      "Generated preview — review the rows below before importing."
  });

  const effectiveDecisions = buildEffectiveDecisions({
    analyses: previewResult?.analyses || [],
    decisions
  });
  const readyToImport = isReadyToImport({
    analyses: previewResult?.analyses || [],
    effectiveDecisions
  });

  const buildPreviewStep = () => ({
    label: "Review and import",
    title: "Review the parsed rows before importing",
    size: "lg",
    noButton: true,
    input: (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={mainTheme}>
          <Box>
            {previewResult && !importResult && (
              <>
                <EmployeeBulkImportPreview
                  previewResult={previewResult}
                  decisions={decisions}
                  setDecisions={setDecisions}
                  companyStartDate={companyStartDate}
                  useCompanyStartDateFallback={useCompanyStartDateFallback}
                  setUseCompanyStartDateFallback={
                    setUseCompanyStartDateFallback
                  }
                />
                <Grid
                  container
                  justifyContent="flex-end"
                  sx={{ mt: 2 }}
                  gap={2}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!readyToImport || previewLoading}
                    onClick={handleImport}
                  >
                    Import as estimated data
                  </Button>
                </Grid>
              </>
            )}
            {importResult && (
              <Box mt={3}>
                <Alert
                  severity={importResult.summary.failed ? "warning" : "success"}
                >
                  <Typography variant="subtitle2">
                    Imported {importResult.summary.created} new and{" "}
                    {importResult.summary.updated} existing employee
                    {importResult.summary.created +
                      importResult.summary.updated ===
                    1
                      ? ""
                      : "s"}{" "}
                    as estimated data.
                  </Typography>
                  {!!importResult.summary.skipped && (
                    <Typography variant="body2">
                      {importResult.summary.skipped} row(s) skipped per your
                      decisions.
                    </Typography>
                  )}
                  {!!importResult.summary.failed && (
                    <Typography variant="body2">
                      {importResult.summary.failed} row(s) failed — check the
                      file and re-upload only those rows.
                    </Typography>
                  )}
                </Alert>
                <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button variant="contained" color="primary" onClick={onClose}>
                    Close
                  </Button>
                </Grid>
              </Box>
            )}
          </Box>
        </ThemeProvider>
      </StyledEngineProvider>
    )
  });

  return previewResult || importResult
    ? [...employeeBulkImportSteps, buildPreviewStep()]
    : employeeBulkImportSteps;
};
